import { Type } from '@sinclair/typebox';
import { createPathGuidParameter } from '../../routes/createPathParameter';
import { okResponse } from '../../routes/defaultResponses';
import { JobTypes } from '../job/JobTypes';

export const workerApiPaths = {
    '/api/workers/job': {
        get: {
            operationId: 'workerApiControllers.getJob',
            responses: {
                '200': {
                    description: 'Result of the operation',
                    content: {
                        'application/json': {
                            schema: Type.Object({
                                jobId: Type.String(),
                                jobType: Type.String(),
                                payload: Type.Any(),
                            }),
                        },
                    },
                },
            },
            parameters: [{ name: 'workerId', in: 'query', schema: Type.String() }],
        },
    },
    '/api/workers/compressedFiles/jobs/{jobId}/getUploadUrl': {
        parameters: [createPathGuidParameter('jobId')],
        post: {
            operationId: 'workerApiControllers.contentReceiveUploadUrl',
            responses: {
                '200': {
                    description: 'Result of the operation',
                    content: {
                        'application/json': {
                            schema: Type.Object({
                                url: Type.String(),
                            }),
                        },
                    },
                },
            },
            requestBody: {
                content: {
                    'application/json': {
                        schema: Type.Object({
                            filename: Type.String(),
                            fileType: Type.String(),
                        }),
                    },
                },
            },
        },
    },
    '/api/workers/compressedFiles/jobs/{jobId}/saveAsset': {
        parameters: [createPathGuidParameter('jobId')],
        post: {
            operationId: 'workerApiControllers.saveAsset',
            responses: {
                '200': {
                    description: 'Result of the operation',
                    content: {
                        'application/json': {
                            schema: Type.Object({
                                ok: Type.Boolean(),
                            }),
                        },
                    },
                },
            },
            requestBody: {
                content: {
                    'application/json': {
                        schema: Type.Object({
                            assetId: Type.String(),
                            objectId: Type.String(),
                            surveyId: Type.String(),
                            path: Type.String(),
                            filename: Type.String(),
                            fileSize: Type.Number(),
                            fileExtension: Type.String(),
                        }),
                    },
                },
            },
        },
    },
    '/api/workers/potreeConvert/jobs/{jobId}/getUploadUrl': {
        parameters: [createPathGuidParameter('jobId')],
        post: {
            operationId: 'workerApiControllers.potreeConvertReceiveUploadUrl',
            responses: {
                '200': {
                    description: 'Result of the operation',
                    content: {
                        'application/json': {
                            schema: Type.Object({
                                url: Type.String(),
                            }),
                        },
                    },
                },
            },
            requestBody: {
                content: {
                    'application/json': {
                        schema: Type.Object({
                            potreeId: Type.String(),
                            filename: Type.String(),
                        }),
                    },
                },
            },
        },
    },
    '/api/workers/potreeConvert/jobs/{jobId}/getUploadUrls': {
        parameters: [createPathGuidParameter('jobId')],
        post: {
            operationId: 'workerApiControllers.potreeConvertReceiveUploadUrls',
            responses: {
                '200': {
                    description: 'Result of the operation',
                    content: {
                        'application/json': {
                            schema: Type.Array(Type.String()),
                        },
                    },
                },
            },
            requestBody: {
                content: {
                    'application/json': {
                        schema: Type.Object({
                            potreeId: Type.String(),
                            filenames: Type.Array(Type.String()),
                        }),
                    },
                },
            },
        },
    },
    '/api/workers/potreeConvert/jobs/{jobId}/getMultiPartUploadUrls': {
        parameters: [createPathGuidParameter('jobId')],
        post: {
            operationId: 'workerApiControllers.potreeConvertReceiveMultiPartUploadUrls',
            responses: {
                '200': {
                    description: 'Result of the operation',
                    content: {
                        'application/json': {
                            schema: Type.Object({
                                uploadPartUrls: Type.Array(Type.String()),
                                uploadId: Type.String(),
                            }),
                        },
                    },
                },
            },
            requestBody: {
                content: {
                    'application/json': {
                        schema: Type.Object({
                            potreeId: Type.String(),
                            filename: Type.String(),
                            numberOfParts: Type.Number(),
                        }),
                    },
                },
            },
        },
    },
    '/api/workers/potreeConvert/jobs/{jobId}/createMultipartUpload': {
        parameters: [createPathGuidParameter('jobId')],
        post: {
            operationId: 'workerApiControllers.potreeConvertCreateMultipartUpload',
            responses: {
                '200': {
                    description: 'Result of the operation',
                    content: {
                        'application/json': {
                            schema: Type.Object({
                                uploadId: Type.String(),
                                nonMultipartUploadUrl: Type.Optional(Type.String()),
                            }),
                        },
                    },
                },
            },
            requestBody: {
                content: {
                    'application/json': {
                        schema: Type.Object({
                            potreeId: Type.String(),
                            filename: Type.String(),
                            numberOfParts: Type.Number(),
                        }),
                    },
                },
            },
        },
    },
    '/api/workers/potreeConvert/jobs/{jobId}/getUploadPartUrl': {
        parameters: [createPathGuidParameter('jobId')],
        post: {
            operationId: 'workerApiControllers.potreeConvertGetUploadPartUrl',
            responses: {
                '200': {
                    description: 'Result of the operation',
                    content: {
                        'application/json': {
                            schema: Type.Object({
                                presignedUrl: Type.String(),
                            }),
                        },
                    },
                },
            },
            requestBody: {
                content: {
                    'application/json': {
                        schema: Type.Object({
                            potreeId: Type.String(),
                            filename: Type.String(),
                            uploadId: Type.String(),
                            partNumber: Type.Number(),
                        }),
                    },
                },
            },
        },
    },
    '/api/workers/potreeConvert/jobs/{jobId}/completeMultiPartUpload': {
        parameters: [createPathGuidParameter('jobId')],
        post: {
            operationId: 'workerApiControllers.potreeConvertCompleteMultiPartUpload',
            responses: {
                ...okResponse,
            },
            requestBody: {
                content: {
                    'application/json': {
                        schema: Type.Object({
                            potreeId: Type.String(),
                            filename: Type.String(),
                            uploadId: Type.String(),
                            completedParts: Type.Array(
                                Type.Object({
                                    eTag: Type.String(),
                                    partNumber: Type.Number(),
                                }),
                            ),
                        }),
                    },
                },
            },
        },
    },
    '/api/workers/{jobType}/jobs/{jobId}/finish': {
        parameters: [{ name: 'jobType', in: 'path', required: true, schema: Type.Enum(JobTypes) }, createPathGuidParameter('jobId')],
        post: {
            operationId: 'workerApiControllers.finishJob',
            responses: {
                '200': {
                    description: 'Result of the operation',
                    content: {
                        'application/json': {
                            schema: Type.Object({
                                ok: Type.Boolean(),
                            }),
                        },
                    },
                },
            },
            requestBody: {
                content: {
                    'application/json': {
                        schema: Type.Object({
                            log: Type.String(),
                            errorLog: Type.String(),
                            isSuccessful: Type.Boolean(),
                            payload: Type.Any(),
                            indexFilename: Type.Optional(Type.String()),
                        }),
                    },
                },
            },
        },
    },
    '/api/workers/{jobType}/jobs/{jobId}/update': {
        parameters: [{ name: 'jobType', in: 'path', required: true, schema: Type.Enum(JobTypes) }, createPathGuidParameter('jobId')],
        post: {
            operationId: 'workerApiControllers.updateJob',
            responses: {
                '200': {
                    description: 'Result of the operation',
                    content: {
                        'application/json': {
                            schema: Type.Object({
                                ok: Type.Boolean(),
                            }),
                        },
                    },
                },
            },
            requestBody: {
                content: {
                    'application/json': {
                        schema: Type.Object({
                            message: Type.String(),
                        }),
                    },
                },
            },
        },
    },
} as const;
